import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PHRASE_LIBRARY, getEventOverrideLine } from '@/config/buddyPhraseLibrary';
import { supabase } from '@/integrations/supabase/client';

function ThoughtBubbleSvg() {
  return (
    <motion.svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      className="shrink-0"
      animate={{ scale: [1, 1.15, 1], opacity: [0.45, 0.65, 0.45] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      <ellipse cx="12" cy="10" rx="9" ry="7" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity={0.08} />
      <circle cx="7" cy="19" r="1.5" fill="currentColor" fillOpacity={0.3} />
      <circle cx="4.5" cy="22" r="1" fill="currentColor" fillOpacity={0.2} />
    </motion.svg>
  );
}

interface BuddyThoughtTickerProps {
  mood: string;
  weather: string;
  networkState: string;
  blockState: string;
  priceState: string;
  verdict: string;
  sentimentBucket?: string | null;
  activeSignature?: string | null;
  blocksToHalving?: number;
  hashrateChange7d?: number;
  lightningVitalityScore?: number;
  footerLine?: string;
  macroState?: string;
  seasonState?: string;
}

export default function BuddyThoughtTicker({
  mood, networkState, blockState,
  sentimentBucket, activeSignature, blocksToHalving, hashrateChange7d, lightningVitalityScore,
  footerLine, macroState, seasonState,
}: BuddyThoughtTickerProps) {
  const [currentThought, setCurrentThought] = useState('');
  const indexRef = useRef(0);
  const poolRef = useRef<string[]>([]);
  const [leaderboardTop3, setLeaderboardTop3] = useState<{ nickname: string; score: number; rank: number }[]>([]);
  const [memoryFragments, setMemoryFragments] = useState<string[]>([]);

  // Fetch top 3 leaderboard entries (refresh every 5 min)
  useEffect(() => {
    let cancelled = false;
    const fetchTop3 = async () => {
      const { data } = await supabase
        .from('game_leaderboard')
        .select('nickname, score')
        .order('score', { ascending: false })
        .limit(3);
      if (!cancelled && data && data.length > 0) {
        setLeaderboardTop3(data.map((d, i) => ({ nickname: d.nickname, score: d.score, rank: i + 1 })));
      }
    };
    fetchTop3();
    const iv = setInterval(fetchTop3, 5 * 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Fetch yesterday's daily summary for memory fragments
  useEffect(() => {
    let cancelled = false;
    const fetchYesterday = async () => {
      const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
      const { data } = await supabase
        .from('buddy_daily_summaries')
        .select('archetype, summary, avg_price, avg_congestion, peak_congestion, event_count')
        .eq('summary_date', yesterday)
        .limit(1)
        .maybeSingle();

      if (cancelled || !data) return;

      const fragments: string[] = [];
      const arch = data.archetype?.toLowerCase() ?? '';

      if (arch.includes('stress') || arch.includes('chaos') || arch.includes('storm')) {
        fragments.push('Yesterday was rough…', 'Still processing yesterday.', 'That storm left marks.');
      } else if (arch.includes('calm') || arch.includes('zen') || arch.includes('quiet')) {
        fragments.push('Yesterday was peaceful.', 'Calm days build strength.', 'Good vibes carry over.');
      } else if (arch.includes('bull') || arch.includes('pump') || arch.includes('rally')) {
        fragments.push('Yesterday had momentum.', 'Still riding that energy.', 'The chart remembers.');
      } else if (arch.includes('bear') || arch.includes('dump') || arch.includes('bleed')) {
        fragments.push('Yesterday hurt.', 'Pain builds conviction.', 'Bears left bruises.');
      } else {
        fragments.push('Yesterday happened.', 'Another day on the chain.');
      }

      if (data.peak_congestion != null && data.peak_congestion > 0.8) {
        fragments.push('Mempool was packed yesterday.', 'Fees were wild.');
      }
      if (data.event_count != null && data.event_count > 10) {
        fragments.push('A lot happened yesterday.', 'Eventful day behind us.');
      } else if (data.event_count != null && data.event_count <= 2) {
        fragments.push('Quiet day yesterday.', 'Not much to report.');
      }
      if (data.avg_price != null) {
        fragments.push(`Yesterday's avg: $${Math.round(data.avg_price).toLocaleString()}.`);
      }

      setMemoryFragments(fragments);
    };
    fetchYesterday();
    const iv = setInterval(fetchYesterday, 60 * 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  useEffect(() => {
    const pool: string[] = [];

    const blockFound = blockState === 'fresh';
    const mempoolJam = networkState === 'congested' || blockState === 'drought';

    const eventLine = getEventOverrideLine({
      blockJustFound: blockFound,
      isMempoolJam: mempoolJam,
      seed: Date.now(),
    });

    if (eventLine) {
      const eventPool = blockFound ? PHRASE_LIBRARY.BLOCK_FOUND : PHRASE_LIBRARY.MEMPOOL_JAM;
      pool.push(...eventPool);
    }

    const macroKey = macroState as keyof typeof PHRASE_LIBRARY;
    if (macroKey && PHRASE_LIBRARY[macroKey]) {
      pool.push(...PHRASE_LIBRARY[macroKey]);
    } else {
      pool.push(...PHRASE_LIBRARY.NEUTRAL);
    }

    if (seasonState === 'cold' || seasonState === 'WINTER') {
      const winter = PHRASE_LIBRARY.WINTER;
      pool.push(winter[0], winter[1], winter[2], winter[3]);
    } else if (seasonState === 'heat' || seasonState === 'HEAT' || seasonState === 'SUMMER') {
      const summer = PHRASE_LIBRARY.SUMMER;
      pool.push(summer[0], summer[1], summer[2], summer[3]);
    }

    if (sentimentBucket === 'extreme_fear' || sentimentBucket === 'fear') {
      pool.push(...PHRASE_LIBRARY.DEEP_VALUE.slice(0, 4));
    } else if (sentimentBucket === 'extreme_greed' || sentimentBucket === 'greed') {
      pool.push(...PHRASE_LIBRARY.HOT.slice(0, 4));
    }

    if (blocksToHalving != null && blocksToHalving < 2016) {
      pool.push(`${blocksToHalving.toLocaleString()} blocks to the next halving`);
    }
    if (hashrateChange7d != null && hashrateChange7d > 5) {
      pool.push("Hashrate climbing. Fortress mode.");
    }
    if (hashrateChange7d != null && hashrateChange7d < -5) {
      pool.push("Miners going quiet.");
    }
    if (lightningVitalityScore != null && lightningVitalityScore > 0.7) {
      pool.push("Lightning lanes open.");
    }

    const sigPhrases: Record<string, string[]> = {
      queueStare: ['Waiting professionally.', 'Queue spiritually unresolved.', 'Still no block.', 'The mempool has opinions.', 'Watching. Quietly.'],
      minerPrayer: ['Praying to the hash gods.', 'Miners, please.', 'Hashboards, do your thing.', 'Where art thou, block?'],
      reliefExhale: ['Morale improved slightly.', 'Block found. Breathing resumes.', 'The chain exhales.', 'That pressure had to break.', 'A good block.'],
      crabDeadpan: ['Sideways. Again.', 'The crab continues.', 'Same chart, new day.', 'Price is a meditation.'],
      laserFlex: ['Conviction mode.', 'Feeling it.', 'Numbers go up. Aura too.', 'This is the way.'],
      feeFeverTwitch: ['These fees though.', 'Fee fever.', 'Mempool is feverish.', 'sat/vB rising. Eyes wide.'],
    };

    // ── Auto-derive signature candidates from live stats ──
    const derivedSignatures = new Set<string>();
    if (activeSignature && sigPhrases[activeSignature]) derivedSignatures.add(activeSignature);

    // queueStare: backlog building
    if (networkState === 'building' || (networkState === 'congested' && blockState !== 'fresh')) {
      derivedSignatures.add('queueStare');
    }
    // minerPrayer: block droughts / delays
    if (blockState === 'delayed' || blockState === 'drought') {
      derivedSignatures.add('minerPrayer');
    }
    // reliefExhale: fresh block or easing pressure
    if (blockState === 'fresh' || networkState === 'easing') {
      derivedSignatures.add('reliefExhale');
    }
    // feeFeverTwitch: congested + stressed mood
    if (networkState === 'congested' && (mood === 'stressed' || mood === 'anxious' || mood === 'shaken')) {
      derivedSignatures.add('feeFeverTwitch');
    }
    // laserFlex: bullish / euphoric energy
    if (mood === 'euphoric' || mood === 'bullish' || mood === 'celebrating' || mood === 'hyped' || sentimentBucket === 'extreme_greed') {
      derivedSignatures.add('laserFlex');
    }
    // crabDeadpan: calm + flat
    if ((mood === 'calm' || mood === 'zen') && (macroState === 'NEUTRAL' || macroState === 'MIXED' || !macroState)) {
      derivedSignatures.add('crabDeadpan');
    }

    // Backend-active signature gets boosted weight; derived ones get normal weight
    derivedSignatures.forEach(sigId => {
      const phrases = sigPhrases[sigId];
      if (!phrases) return;
      const tagged = phrases.map(p => `⚡${sigId}|${p}`);
      const weight = sigId === activeSignature ? 4 : 2;
      for (let i = 0; i < weight; i++) pool.push(...tagged);
    });

    if (footerLine) {
      pool.push(footerLine, footerLine, footerLine);
    }

    pool.push(PHRASE_LIBRARY.GENERAL_MAXI[Math.floor(Math.random() * PHRASE_LIBRARY.GENERAL_MAXI.length)]);
    pool.push(PHRASE_LIBRARY.GENERAL_MAXI[Math.floor(Math.random() * PHRASE_LIBRARY.GENERAL_MAXI.length)]);

    // Memory fragments — tagged with 🔮 prefix for visual detection
    if (memoryFragments.length > 0) {
      const shuffled = [...memoryFragments].sort(() => Math.random() - 0.5);
      const picks = shuffled.slice(0, 3);
      for (const frag of picks) {
        const tagged = `🔮${frag}`;
        pool.push(tagged, tagged);
      }
    }

    // Leaderboard shoutouts
    if (leaderboardTop3.length > 0) {
      const congrats = [
        (n: string, r: number) => `#${r} ${n} — respect earned.`,
        (n: string, r: number) => `${n} holds #${r}. Impressive block.`,
        (n: string, r: number) => `Top ${r}: ${n}. Proof of skill.`,
        (n: string, r: number) => `${n} at #${r}. The leaderboard remembers.`,
        (n: string, r: number) => `Congrats ${n} — rank #${r} miner.`,
        (n: string, r: number) => `${n} built a legendary block. #${r}.`,
      ];
      const medals = ['🥇', '🥈', '🥉'];
      for (const pick of leaderboardTop3) {
        const template = congrats[Math.floor(Math.random() * congrats.length)];
        const medal = medals[pick.rank - 1] ?? '🏆';
        const line = `${medal}${template(pick.nickname, pick.rank)}`;
        pool.push(line, line, line);
      }
    }

    // Shuffle
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    poolRef.current = pool;
    indexRef.current = 0;
    setCurrentThought(pool[0] ?? '');
  }, [mood, blockState, networkState, footerLine, macroState, seasonState, leaderboardTop3, memoryFragments, activeSignature, sentimentBucket]);

  // Rotate thoughts every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const pool = poolRef.current;
      if (pool.length === 0) return;
      indexRef.current = (indexRef.current + 1) % pool.length;
      setCurrentThought(pool[indexRef.current]);
    }, 10_000);
    return () => clearInterval(interval);
  }, []);

  // Detect thought type
  const isMemoryFragment = currentThought.startsWith('🔮');
  const medalMatch = currentThought.match(/^(🥇|🥈|🥉)/);
  const isLeaderboardShoutout = !!medalMatch;
  const medal = medalMatch?.[1] ?? '';
  const sigMatch = currentThought.match(/^⚡([a-zA-Z]+)\|(.+)$/);
  const isSignatureThought = !!sigMatch;
  const signatureId = sigMatch?.[1] ?? '';

  const displayText = isLeaderboardShoutout
    ? currentThought.slice(medal.length)
    : isMemoryFragment
    ? currentThought.slice(2)
    : isSignatureThought
    ? sigMatch![2]
    : currentThought;

  const medalGradient = medal === '🥇'
    ? 'linear-gradient(90deg, hsl(43 90% 55%), hsl(38 85% 65%), hsl(43 90% 55%))'
    : medal === '🥈'
    ? 'linear-gradient(90deg, hsl(220 10% 65%), hsl(220 15% 78%), hsl(220 10% 65%))'
    : 'linear-gradient(90deg, hsl(25 60% 48%), hsl(30 55% 60%), hsl(25 60% 48%))';

  const medalGlow = medal === '🥇'
    ? 'drop-shadow(0 0 4px hsl(43 80% 50% / 0.3))'
    : medal === '🥈'
    ? 'drop-shadow(0 0 4px hsl(220 15% 70% / 0.3))'
    : 'drop-shadow(0 0 4px hsl(25 50% 45% / 0.3))';

  const memoryGradient = 'linear-gradient(90deg, hsl(270 45% 65%), hsl(250 40% 72%), hsl(280 50% 68%), hsl(270 45% 65%))';

  // Per-signature visual identity (cyan/teal "neural" theme distinct from violet memory + bitcoin standard)
  const SIG_VISUALS: Record<string, { icon: string; hue: string; hue2: string; label: string }> = {
    queueStare:     { icon: '👁', hue: '195 80% 60%', hue2: '180 70% 55%', label: 'Queue Stare' },
    minerPrayer:    { icon: '🙏', hue: '210 75% 65%', hue2: '230 65% 60%', label: 'Miner Prayer' },
    reliefExhale:   { icon: '💨', hue: '160 65% 55%', hue2: '180 70% 60%', label: 'Relief Exhale' },
    crabDeadpan:    { icon: '🦀', hue: '15 55% 55%',  hue2: '0 50% 60%',   label: 'Crab Deadpan' },
    laserFlex:      { icon: '🔆', hue: '320 75% 65%', hue2: '280 70% 65%', label: 'Laser Flex' },
    feeFeverTwitch: { icon: '⚡', hue: '45 95% 60%',  hue2: '20 90% 60%',  label: 'Fee Fever' },
  };
  const sigVisual = isSignatureThought ? (SIG_VISUALS[signatureId] ?? SIG_VISUALS.queueStare) : null;
  const signatureGradient = sigVisual
    ? `linear-gradient(90deg, hsl(${sigVisual.hue}), hsl(${sigVisual.hue2}), hsl(${sigVisual.hue}))`
    : '';

  // Mood-reactive glow color
  const moodGlow = useMemo(() => {
    switch (mood) {
      case 'euphoric': case 'bullish':
        return { color: 'hsl(43 90% 55%)', alpha: 0.18, speed: 2 };
      case 'hopeful': case 'optimistic':
        return { color: 'hsl(var(--success))', alpha: 0.14, speed: 3 };
      case 'anxious': case 'stressed':
        return { color: 'hsl(var(--stress))', alpha: 0.16, speed: 1.8 };
      case 'fearful': case 'capitulation':
        return { color: 'hsl(0 68% 50%)', alpha: 0.2, speed: 1.5 };
      case 'zen': case 'calm':
        return { color: 'hsl(var(--energy))', alpha: 0.12, speed: 4 };
      default:
        return { color: 'hsl(var(--bitcoin))', alpha: 0.1, speed: 3.5 };
    }
  }, [mood]);

  // Icon per thought type
  const thoughtIcon = isLeaderboardShoutout ? (
    <motion.span
      className="text-[14px] shrink-0"
      animate={{ scale: [1, 1.2, 1], rotate: [0, 8, -8, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      {medal}
    </motion.span>
  ) : isMemoryFragment ? (
    <motion.span
      className="text-[12px] shrink-0"
      animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.1, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      style={{ filter: 'drop-shadow(0 0 4px hsl(270 50% 60% / 0.5))' }}
    >
      🔮
    </motion.span>
  ) : isSignatureThought && sigVisual ? (
    <motion.span
      className="text-[13px] shrink-0"
      animate={{ scale: [1, 1.18, 1], rotate: [-3, 3, -3] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
      style={{ filter: `drop-shadow(0 0 6px hsl(${sigVisual.hue} / 0.6))` }}
    >
      {sigVisual.icon}
    </motion.span>
  ) : (
    <motion.span
      className="text-[11px] shrink-0"
      style={{ color: 'hsl(var(--bitcoin) / 0.5)' }}
      animate={{ opacity: [0.4, 0.7, 0.4] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      💭
    </motion.span>
  );

  // Text style per thought type
  const textStyle: React.CSSProperties = isLeaderboardShoutout ? {
    background: medalGradient,
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'leaderboard-shimmer 3s linear infinite',
    filter: medalGlow,
  } : isMemoryFragment ? {
    background: memoryGradient,
    backgroundSize: '300% 100%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'memory-drift 6s linear infinite',
    filter: 'drop-shadow(0 0 6px hsl(270 50% 60% / 0.2))',
  } : isSignatureThought && sigVisual ? {
    background: signatureGradient,
    backgroundSize: '250% 100%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'signature-pulse 4s linear infinite',
    filter: `drop-shadow(0 0 5px hsl(${sigVisual.hue} / 0.35))`,
    letterSpacing: '0.02em',
  } : {
    color: 'hsl(var(--foreground) / 0.7)',
    textShadow: '0 0 12px hsl(var(--bitcoin) / 0.08)',
  };

  return (
    <motion.div
      className="w-full overflow-hidden flex items-center justify-center gap-2 py-2 px-3 rounded-xl border relative"
      animate={{
        boxShadow: [
          `0 0 8px ${moodGlow.color.replace(')', ` / ${moodGlow.alpha * 0.4})`)}, inset 0 1px 0 hsl(0 0% 100% / 0.04)`,
          `0 0 16px ${moodGlow.color.replace(')', ` / ${moodGlow.alpha})`)}, 0 0 4px ${moodGlow.color.replace(')', ` / ${moodGlow.alpha * 0.6})`)}, inset 0 1px 0 hsl(0 0% 100% / 0.04)`,
          `0 0 8px ${moodGlow.color.replace(')', ` / ${moodGlow.alpha * 0.4})`)}, inset 0 1px 0 hsl(0 0% 100% / 0.04)`,
        ],
        borderColor: isMemoryFragment
          ? ['hsl(270 40% 50% / 0.15)', 'hsl(270 40% 50% / 0.35)', 'hsl(270 40% 50% / 0.15)']
          : isSignatureThought && sigVisual
          ? [`hsl(${sigVisual.hue} / 0.18)`, `hsl(${sigVisual.hue} / 0.45)`, `hsl(${sigVisual.hue} / 0.18)`]
          : [
            moodGlow.color.replace(')', ' / 0.1)'),
            moodGlow.color.replace(')', ' / 0.25)'),
            moodGlow.color.replace(')', ' / 0.1)'),
          ],
      }}
      transition={{ duration: isMemoryFragment ? 4 : isSignatureThought ? 2.6 : moodGlow.speed, repeat: Infinity, ease: 'easeInOut' }}
      style={{
        minHeight: 32,
        background: isMemoryFragment
          ? 'linear-gradient(135deg, hsl(270 25% 12% / 0.5), hsl(250 20% 14% / 0.35))'
          : isSignatureThought && sigVisual
          ? `linear-gradient(135deg, hsl(${sigVisual.hue} / 0.10), hsl(${sigVisual.hue2} / 0.06))`
          : 'linear-gradient(135deg, hsl(var(--foreground) / 0.03), hsl(var(--bitcoin) / 0.04))',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Signature label badge */}
      {isSignatureThought && sigVisual && (
        <motion.span
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 0.75, x: 0 }}
          className="absolute top-0.5 right-2 text-[8px] font-bold tracking-widest uppercase pointer-events-none"
          style={{ color: `hsl(${sigVisual.hue} / 0.85)`, textShadow: `0 0 4px hsl(${sigVisual.hue} / 0.4)` }}
        >
          {sigVisual.label}
        </motion.span>
      )}
      {thoughtIcon}
      <AnimatePresence mode="wait">
        <motion.p
          key={currentThought}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="text-[11px] font-body font-semibold text-center italic leading-snug"
          style={textStyle}
        >
          {displayText}
        </motion.p>
      </AnimatePresence>
      <style>{`
        @keyframes leaderboard-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
        @keyframes memory-drift { from { background-position: 300% 0; } to { background-position: -300% 0; } }
        @keyframes signature-pulse { from { background-position: 250% 0; } to { background-position: -250% 0; } }
      `}</style>
    </motion.div>
  );
}
